<template>
  <div :class="device.isMobile ? 'py-2' : 'px-4 pb-2 pt-4'">
    <!-- Zbirno / po poslovnici (samo kad firma ima više poslovnica) -->
    <div v-if="hasBranches && !isLoading && selectedRestaurants.length && meals.length" class="mb-3">
      <v-btn-toggle v-model="view" mandatory dense color="primary">
        <v-btn v-for="option in viewOptions" :key="option.value" :value="option.value" small>
          {{ option.label }}
        </v-btn>
      </v-btn-toggle>
    </div>

    <!-- Loading state -->
    <empty-state v-if="isLoading" :title="$t('loading')" :loading="true" />

    <!-- Empty state: No restaurants selected -->
    <empty-state
      v-else-if="!selectedRestaurants.length"
      icon="mdi-store"
      :title="$t('no_restaurants_selected')"
      :subtitle="$t('select_restaurants_to_view_report')"
    />

    <!-- Empty state: No data -->
    <empty-state
      v-else-if="!meals.length"
      icon="mdi-food-off"
      :title="$t('no_data')"
      :subtitle="$t('no_meals_for_selected_period')"
    />

    <!-- Desktop View: jedna tabela (zbirno) ili tabela za svaku poslovnicu -->
    <div v-else-if="!device.isMobile">
      <div v-for="table in tables" :key="table.key" :class="{ 'mb-8': byBranch }">
        <div v-if="byBranch" class="text-subtitle-1 font-weight-medium mb-2">
          {{ toLatin(table.name) }}
        </div>
        <v-data-table
          :headers="headers"
          :items="customSearch(table.meals, headers, search)"
          class="ordera-box-shadow striped-table"
          hide-default-footer
          disable-pagination
          mobile-breakpoint="0"
        >
          <template v-slot:item.name="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">
                  {{ toLatin(item.name) }}
                </span>
              </template>
              <span>{{ item.id }}</span>
            </v-tooltip>
          </template>
          <template v-slot:item.type="{ item }">
            {{ typeText(item.type) }}
          </template>
          <template #body.append>
            <tr class="font-weight-bold totals-row">
              <td colspan="2" v-if="!device.isMobile"></td>

              <td>{{ $t("total") }}</td>
              <td v-for="shift in shiftsWithData" :key="shift" class="text-center">
                {{ formatShiftQuantity(table.totals[getShiftKey(shift)]) }}
              </td>
              <td class="text-center">{{ table.totals.total }}</td>
            </tr>
          </template>
          <!-- Dynamic shift templates -->
          <template
            v-for="shift in shiftsWithData"
            v-slot:[`item.${getShiftKey(shift)}`]="{ item }"
          >
            <span>
              <span v-if="item[getShiftKey(shift)].customerQuantity > 0">
                {{ item[getShiftKey(shift)].customerQuantity }}
              </span>
              <span v-if="item[getShiftKey(shift)].restaurantQuantity > 0">
                <span v-if="item[getShiftKey(shift)].customerQuantity > 0"> + </span>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on"
                      >{{ item[getShiftKey(shift)].restaurantQuantity }}R</span
                    >
                  </template>
                  <span>{{ $t("ordered_by_restaurant") }}</span>
                </v-tooltip>
              </span>
              <span v-if="item[getShiftKey(shift)].totalQuantity === 0">0</span>
            </span>
          </template>
        </v-data-table>
      </div>
    </div>

    <!-- Mobile View -->
    <div v-else>
      <mobile-data-cards
        v-for="table in tables"
        :key="table.key"
        :title="byBranch ? toLatin(table.name) : undefined"
        :headers="mobileHeaders"
        :items="customSearch(table.meals, headers, search)"
        item-key="id"
        :fields-per-row="null"
        :bold-fields="['name']"
        :show-row-number="false"
        class="px-3"
      >
        <!-- Dynamic shift templates for mobile -->
        <template
          v-for="shift in shiftsWithData"
          v-slot:[`item.${getShiftKey(shift)}`]="{ item }"
        >
          <span>
            <span v-if="item[getShiftKey(shift)].customerQuantity > 0">
              {{ item[getShiftKey(shift)].customerQuantity }}
            </span>
            <span v-if="item[getShiftKey(shift)].restaurantQuantity > 0">
              <span v-if="item[getShiftKey(shift)].customerQuantity > 0"> + </span>
              {{ item[getShiftKey(shift)].restaurantQuantity }}R
            </span>
            <span v-if="item[getShiftKey(shift)].totalQuantity === 0">0</span>
          </span>
        </template>
      </mobile-data-cards>
    </div>
  </div>
</template>

<script>
import reportService from "@/services/report";
import hasPrinting from "@/mixins/hasPrinting";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import EmptyState from "@/components/global/EmptyState.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import hasExcel from "@/mixins/hasExcel";
import hasSnackbar from "@/mixins/hasSnackbar";
import hasCompanyBranches from "@/mixins/report/hasCompanyBranches";
import { aggregateMealEntries } from "./aggregateMeals";
import hasMobileHeaders from "@/mixins/report/hasMobileHeaders";
import { SHIFTS, getShiftKey } from "@/constants/shifts";

export default {
  name: "CompanyMultiRestaurantTabRestaurantMeals",
  mixins: [hasPrinting, hasReportPrinting, hasExcel, hasSnackbar, hasMobileHeaders, hasCompanyBranches],
  components: {
    EmptyState,
    MobileDataCards,
  },
  data() {
    return {
      packages: null,
      isLoading: false,
      view: "total",
    };
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    getShiftKey,
    createEmptyShift() {
      return { customerQuantity: 0, restaurantQuantity: 0, totalQuantity: 0 };
    },
    formatShiftQuantity(shiftData) {
      if (!shiftData || shiftData.totalQuantity === 0) return "0";
      const { customerQuantity = 0, restaurantQuantity = 0 } = shiftData;
      const parts = [];
      if (customerQuantity > 0) parts.push(customerQuantity);
      if (restaurantQuantity > 0) parts.push(`${restaurantQuantity}R`);
      return parts.join(" + ") || "0";
    },
    /** Tip obroka iz izveštaja ("package", "meal", "food") prevedeno; nepoznat tip ostaje kakav je. */
    typeText(type) {
      return ["package", "meal", "food"].includes(type) ? this.$t(type) : type || "";
    },
    /** Redovi za štampu i Excel; po poslovnici je jedna tabela sa kolonom poslovnice. */
    getFormattedItems() {
      const shifts = (item) =>
        this.shiftsWithData.reduce((acc, shift) => {
          acc[this.getShiftKey(shift)] = this.formatShiftQuantity(item[this.getShiftKey(shift)]);
          return acc;
        }, {});
      if (!this.byBranch) {
        return this.meals.map((item, index) => ({
          num: index + 1,
          name: item.name,
          type: this.typeText(item.type),
          ...shifts(item),
          total: item.total,
        }));
      }
      let num = 0;
      return this.branchGroups.flatMap((group) =>
        group.meals.map((item) => ({
          num: ++num,
          company_name: this.toLatin(group.name),
          name: item.name,
          type: this.typeText(item.type),
          ...shifts(item),
          total: item.total,
        }))
      );
    },
    /** Zbir obroka po smenama i ukupno. */
    totalsOf(meals) {
      const totals = SHIFTS.reduce(
        (acc, s) => ({ ...acc, [s.key]: this.createEmptyShift() }),
        { total: 0 }
      );

      meals.forEach((meal) => {
        SHIFTS.forEach((shift) => {
          const shiftKey = shift.key;
          totals[shiftKey].customerQuantity += meal[shiftKey]?.customerQuantity || 0;
          totals[shiftKey].restaurantQuantity += meal[shiftKey]?.restaurantQuantity || 0;
          totals[shiftKey].totalQuantity += meal[shiftKey]?.totalQuantity || 0;
        });
        totals.total += meal.total || 0;
      });

      return totals;
    },
    aggregateMeals(entries) {
      return aggregateMealEntries(entries, (value) => this.toLatin(value));
    },
    async fetchData() {
      if (!this.companyId || this.selectedRestaurants.length === 0) {
        this.packages = [];
        return;
      }

      this.isLoading = true;
      try {
        const params = {
          date: this.dates.from,
          date2: this.dates.to,
          company: this.companyId,
        };

        // Fetch data for each selected restaurant
        const promises = this.selectedRestaurants.map((restaurantId) =>
          reportService.getPackagesForMoreRestaurant(restaurantId, params)
        );

        const responses = await Promise.all(promises);

        // Odgovor restorana ima po jednu stavku za svaku poslovnicu firme (ključ je naziv firme). Zadržava se svaka,
        // inače zbir i izbor poslovnice izgube sve osim prve.
        this.packages = responses.flatMap((response, index) =>
          (Array.isArray(response) ? response : []).map((company) => ({
            ...company,
            id: this.selectedRestaurants[index],
            company_name: company.name || "",
          }))
        );
      } catch (error) {
        console.error("Error fetching meals:", error);
        this.showErrorSnackbar(error, this.$t("error_loading_report"));
      } finally {
        this.isLoading = false;
      }
    },
  },
  computed: {
    companyId() {
      return this.$store.state.company?.id;
    },
    restaurants() {
      return this.$store.state.company?.restaurants || [];
    },
    selectedRestaurants() {
      return this.$store.state.report.selectedRestaurants || [];
    },
    dates() {
      const { from, to } = this.$store.state.report.filters.dates;
      return { from, to };
    },
    selectedShifts() {
      return this.$store.state.report.filters.selectedShifts;
    },
    search() {
      return this.$store.state.report.filters.search;
    },
    /** Stavke odgovora (restoran × poslovnica) u izabranim poslovnicama. */
    filteredPackages() {
      return Array.isArray(this.packages)
        ? this.packages.filter((entry) => this.branchNameSelected(entry.company_name))
        : [];
    },
    meals() {
      return this.aggregateMeals(this.filteredPackages);
    },
    totals() {
      return this.totalsOf(this.meals);
    },
    /** Zbirno (podrazumevano) ili po poslovnici; po poslovnici ima smisla samo kad firma ima više poslovnica. */
    byBranch() {
      return this.hasBranches && this.view === "branch";
    },
    viewOptions() {
      return [
        { value: "total", label: this.$t("recipes_shell.tabs.total") },
        { value: "branch", label: this.$t("report_desktop.by_branch") },
      ];
    },
    /** Obroci svake poslovnice: [{ key, name, meals }] u redosledu iz odgovora. */
    branchGroups() {
      const groups = new Map();
      this.filteredPackages.forEach((entry) => {
        const name = entry.company_name || "";
        groups.set(name, [...(groups.get(name) || []), entry]);
      });
      return [...groups.entries()]
        .map(([name, entries]) => ({ key: name || "-", name, meals: this.aggregateMeals(entries) }))
        .filter((group) => group.meals.length);
    },
    /** Tabele koje se prikazuju: jedna zbirna ili po jedna za poslovnicu (sa zbirom). */
    tables() {
      const groups = this.byBranch ? this.branchGroups : [{ key: "all", name: "", meals: this.meals }];
      return groups.map((group) => ({ ...group, totals: this.totalsOf(group.meals) }));
    },
    /** Zaglavlja za štampu i Excel: po poslovnici uz kolonu poslovnice. */
    exportHeaders() {
      return this.byBranch
        ? [this.headers[0], { text: this.$t("branch"), value: "company_name" }, ...this.headers.slice(1)]
        : this.headers;
    },
    shiftsWithData() {
      // selectedShifts is an array from store [1, 2, 3] or empty []
      if (Array.isArray(this.selectedShifts) && this.selectedShifts.length > 0) {
        return this.selectedShifts.sort((a, b) => a - b);
      }
      // If no shifts selected, show all
      return [1, 2, 3, 4];
    },
    headers() {
      const baseHeaders = [
        { text: this.$t("r_b"), value: "num", sortable: false },
        { text: this.$t("meal"), value: "name" },
        { text: this.$t("type"), value: "type" },
      ];

      const shiftHeaders = SHIFTS.filter((shift) =>
        this.shiftsWithData.includes(shift.id)
      ).map((shift) => ({
        text: this.$t(shift.label),
        value: shift.key,
        align: "center",
      }));

      const totalHeader = [{ text: this.$t("total"), value: "total", align: "center" }];

      return [...baseHeaders, ...shiftHeaders, ...totalHeader];
    },
    mobileHeaders() {
      const baseHeaders = [{ text: this.$t("meal"), value: "name", cols: 12 }];

      const shiftHeaders = SHIFTS.filter((shift) =>
        this.shiftsWithData.includes(shift.id)
      ).map((shift) => ({
        text: this.$t(shift.label),
        value: shift.key,
        align: "center",
        cols: 3,
      }));

      const totalHeader = [
        { text: this.$t("total"), value: "total", align: "center", cols: 3 },
      ];

      return [...baseHeaders, ...shiftHeaders, ...totalHeader];
    },
  },
  watch: {
    dates: {
      handler() {
        this.fetchData();
      },
      deep: true,
    },
    selectedRestaurants: {
      handler() {
        this.fetchData();
      },
      deep: true,
    },
    isExcelDownloading() {
      if (!this.isExcelDownloading) return;
      this.downloadExcel({
        fileName: [this.$t("report"), this.$t("meals")].join(" ") + ".xlsx",
        sheets: [
          {
            name: this.$t("meals"),
            headers: this.exportHeaders,
            items: this.getFormattedItems(),
            title: [this.$t("report"), this.$t("meals")].join(" "),
            subtitle: this.subtitle,
          },
        ],
      });
    },
    isPrinting(isPrinting) {
      if (!isPrinting) return;
      const restaurantNames = this.selectedRestaurants
        .map((restaurantId) => {
          const restaurant = this.restaurants.find((r) => r.id === restaurantId);
          return restaurant ? this.toLatin(restaurant.name) : "";
        })
        .filter(Boolean)
        .join(", ");

      this.printDocument({
        tables: [
          { headers: this.exportHeaders, items: this.getFormattedItems(), showCaption: true },
        ],
        title: [this.$t("report"), this.$t("meals")].join(": "),
        subtitle: this.subtitle,
        afterTableHtml: `<div style="margin-top: 20px; padding-top: 10px; border-top: 1px solid #ddd;">
          <strong>${this.$t("restaurants")}:</strong> ${restaurantNames}
        </div>`,
        hideDatetime: true,
      });
    },
  },
};
</script>
